import type {
  CarePlan,
  CarePlanActivity,
  PlanDefinition,
  PlanDefinitionAction,
  ServiceRequest,
} from '@medplum/fhirtypes';

import {
  ACTIVITY_PHASE_EXTENSION_URL,
  ACTIVITY_STICK_EXTENSION_URL,
  CAREPLAN_SEGMENT_SYSTEMS,
  PLAN_SHAPE_EXTENSION_URL,
  isPhaseSystem,
} from './constants';
import { fromDosageExtension, fromTiming } from './shared';
import { PLAN_SHAPE_CODES } from './spec';
import type { PlanActivity, PlanShapeCode } from './spec';

// The canonical reference a CarePlan instantiates: `{url}|{version}`.
export function planCanonical(pd: PlanDefinition): string | undefined {
  if (!pd.url) {
    return undefined;
  }
  return pd.version ? `${pd.url}|${pd.version}` : pd.url;
}

// Canonical URLs (version-stripped) of the ActivityDefinitions a plan's action
// tree references — what callers fetch (one `url=a,b,c` search) to seed the
// instance spec with each exercise's intrinsic fields.
export function referencedActivityCanonicals(pd: PlanDefinition): string[] {
  const urls = new Set<string>();
  const walk = (actions: PlanDefinitionAction[] | undefined) => {
    for (const action of actions ?? []) {
      const canonical = action.definitionCanonical?.split('|')[0];
      if (canonical) {
        urls.add(canonical);
      }
      walk(action.action);
    }
  };
  walk(pd.action);
  return [...urls];
}

function isShapeCode(code: string | undefined): code is PlanShapeCode {
  return (
    code !== undefined && (PLAN_SHAPE_CODES as readonly string[]).includes(code)
  );
}

export function carePlanShapeOf(carePlan: CarePlan): PlanShapeCode | undefined {
  const code = carePlan.extension?.find(
    (e) => e.url === PLAN_SHAPE_EXTENSION_URL,
  )?.valueCode;
  return isShapeCode(code) ? code : undefined;
}

export function phaseCoding(action: PlanDefinitionAction) {
  for (const cc of action.code ?? []) {
    const coding = (cc.coding ?? []).find((c) => isPhaseSystem(c.system));
    if (coding) {
      return coding;
    }
  }
  return undefined;
}

export function actionDurationDays(
  action: PlanDefinitionAction,
): number | undefined {
  const fromTimingTiming = fromTiming(action.timingTiming).durationDays;
  if (fromTimingTiming !== undefined) {
    return fromTimingTiming;
  }
  return action.timingDuration?.code === 'd'
    ? action.timingDuration.value
    : undefined;
}

// Authored/seeded exit gates carry pseudo-FHIRPath like
// `nprs <= 3 and visitCount >= 6`, so the `visitCount >= N` clause is the
// target visit count. Used to seed the phase stick's `quantity`. Only `>=` is
// matched (the form authored/seeded gates use); absent ⇒ no visit target.
export function actionVisitTarget(
  action: PlanDefinitionAction,
): number | undefined {
  for (const condition of action.condition ?? []) {
    const match = condition.expression?.expression?.match(
      /visitCount\s*>=\s*(\d+)/,
    );
    if (match) return Number(match[1]);
  }
  return undefined;
}

export function addDays(isoDate: string, days: number): string {
  const d = new Date(`${isoDate}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export function diffDays(startIsoDate: string, endIsoDate: string): number {
  const ms =
    new Date(`${endIsoDate}T00:00:00Z`).getTime() -
    new Date(`${startIsoDate}T00:00:00Z`).getTime();
  return Math.round(ms / 86_400_000);
}

// A decoded instance activity: a PlanActivity enriched with what only the
// patient-bound ServiceRequest knows — its template derivation (variance is
// computed off `template`'s presence, ADR-0013) and its status (active =
// actionable now; draft = a future phase's materialized-but-not-yet request).
export type InstanceActivity = PlanActivity & {
  // SR.instantiatesCanonical — the template activity this derives from.
  // Absent = ad-hoc.
  template?: string;
  status?: ServiceRequest['status'];
  // The materialized ServiceRequest's id, present only on instance-sourced
  // (ServiceRequest-decoded) activities.
  serviceRequestId?: string;
};

export function serviceRequestPhaseCoding(sr: ServiceRequest) {
  return sr.extension?.find((e) => e.url === ACTIVITY_PHASE_EXTENSION_URL)
    ?.valueCoding;
}

// A spine ServiceRequest (ADR-0016): the per-phase stick that organizes the
// instance and anchors visit attribution, NOT a prescribed leaf activity.
// Identified by its discriminator `category` coding (the `careplan-segment`
// system, orchard's atlas twin accepted on read) — distinct from the leaf SRs'
// `careplan-activity-phase` extension. The same `based-on=CarePlan` fetch
// returns both, so leaf decodes skip-guard on this.
export function isSpineStick(sr: ServiceRequest): boolean {
  return (sr.category ?? []).some((cc) =>
    (cc.coding ?? []).some((c) =>
      (CAREPLAN_SEGMENT_SYSTEMS as readonly string[]).includes(c.system ?? ''),
    ),
  );
}

// The stick reference a CarePlan.activity carries. Written to the
// careplan-activity-stick extension (cpl-3 forbids activity.reference beside
// detail); orchard-written legacy plans carry activity.reference instead, so
// both are read.
export function activityStickRef(
  activity: CarePlanActivity,
): string | undefined {
  const ext = activity.extension?.find(
    (e) => e.url === ACTIVITY_STICK_EXTENSION_URL,
  );
  return ext?.valueReference?.reference ?? activity.reference?.reference;
}

// The full phase coding a spine stick organizes — carried alongside the
// discriminator in its `category`. The Encounter.type mirror copies this
// coding verbatim so encounter-phase readers match it back to the spine.
export function serviceRequestSegmentPhaseCoding(sr: ServiceRequest) {
  for (const cc of sr.category ?? []) {
    const coding = (cc.coding ?? []).find((c) => isPhaseSystem(c.system));
    if (coding) return coding;
  }
  return undefined;
}

// One prescribed activity back out of its patient-bound ServiceRequest — the
// instance-sourced half of the decode flip (ADR-0013): tailored instances
// render from these, never from the template's action tree.
export function serviceRequestToActivity(
  sr: ServiceRequest,
): InstanceActivity | null {
  const coding = sr.code?.coding?.[0];
  const name = sr.code?.text ?? coding?.display;
  if (!name) {
    return null;
  }
  const canonical = sr.instantiatesCanonical?.[0];
  return {
    name,
    code: coding
      ? {
          text: coding.display ?? name,
          system: coding.system,
          code: coding.code,
        }
      : undefined,
    description: sr.note?.[0]?.text,
    dosage: fromDosageExtension(sr.extension),
    frequency: fromTiming(sr.occurrenceTiming).cadence,
    patientInstructions: sr.patientInstruction,
    template: canonical,
    status: sr.status,
    ...(sr.id ? { serviceRequestId: sr.id } : {}),
  };
}

// Seeded instances carry the phase on CarePlan.activity.detail.code; tailored
// instances also stamp it on a CarePlan.activity extension. The decode accepts both.
export function activityPhaseCoding(activity: CarePlanActivity) {
  const detailCoding = activity.detail?.code?.coding?.find((c) =>
    isPhaseSystem(c.system),
  );
  if (detailCoding) {
    return detailCoding;
  }
  const ext = activity.extension?.find(
    (e) => e.url === ACTIVITY_PHASE_EXTENSION_URL,
  );
  return ext?.valueCoding;
}

// The instance's prescribed activities grouped by phase code — the
// instance-sourced half of the decode flip. Revoked/errored requests drop out.
// Shared with `decodeInstanceSpec` so the renderer and the spec decode can never
// disagree on which requests count.
export function instanceActivitiesByPhase(
  serviceRequests: ServiceRequest[] | undefined,
): Map<string, InstanceActivity[]> {
  const map = new Map<string, InstanceActivity[]>();
  for (const sr of serviceRequests ?? []) {
    if (sr.status === 'revoked' || sr.status === 'entered-in-error') {
      continue;
    }
    // Spine sticks organize the phases; they are never prescribed activities.
    if (isSpineStick(sr)) {
      continue;
    }
    const code = serviceRequestPhaseCoding(sr)?.code;
    if (!code) {
      continue;
    }
    const activity = serviceRequestToActivity(sr);
    if (!activity) {
      continue;
    }
    const list = map.get(code) ?? [];
    list.push(activity);
    map.set(code, list);
  }
  return map;
}
